// src/context/PermissionsContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import logger from '../utils/logger';

const PermissionsContext = createContext();

/**
 * Provider لإدارة صلاحيات المستخدم الحالي
 */
export const PermissionsProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  // تحميل الصلاحيات من بيانات المستخدم المخزنة
  useEffect(() => {
    loadUserPermissions();
  }, []);

  const loadUserPermissions = () => {
    try {
      // الحصول على بيانات المستخدم من localStorage أو sessionStorage
      const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
      
      if (userStr) {
        const user = JSON.parse(userStr);
        
        // إذا كان المستخدم مدير، له صلاحيات كاملة
        if (user.role === 'admin' || user.role === 'super_admin') {
          // يمكن إضافة جميع الصلاحيات أو تركها فارغة والتحقق من الدور
          setPermissions(user.permissions || ['*']); // '*' تعني جميع الصلاحيات
        } else {
          // للأدوار الأخرى، استخدم الصلاحيات المخزنة
          setPermissions(user.permissions || []);
        }
      }
      
      setLoading(false);
    } catch (error) {
      logger.error('Error loading user permissions:', error);
      setPermissions([]);
      setLoading(false);
    }
  };

  /**
   * التحقق من امتلاك المستخدم لصلاحية معينة
   * @param {string} permission - الصلاحية المطلوبة
   * @returns {boolean}
   */
  const hasPermission = (permission) => {
    if (!permission) return true; // إذا لم تحدد صلاحية، السماح بالوصول
    
    // التحقق من وجود صلاحية كاملة (*)
    if (permissions.includes('*')) return true;
    
    // التحقق من الصلاحية المحددة
    return permissions.includes(permission);
  };

  /**
   * التحقق من امتلاك المستخدم لأي صلاحية من قائمة
   * @param {Array} permissionList - قائمة الصلاحيات
   * @returns {boolean}
   */
  const hasAnyPermission = (permissionList) => {
    if (!permissionList || permissionList.length === 0) return true;
    if (permissions.includes('*')) return true;
    
    return permissionList.some(permission => permissions.includes(permission));
  };

  /**
   * التحقق من امتلاك المستخدم لجميع الصلاحيات من قائمة
   * @param {Array} permissionList - قائمة الصلاحيات
   * @returns {boolean}
   */
  const hasAllPermissions = (permissionList) => {
    if (!permissionList || permissionList.length === 0) return true;
    if (permissions.includes('*')) return true;
    
    return permissionList.every(permission => permissions.includes(permission));
  };

  /**
   * تحديث الصلاحيات (بعد تحديثها من الخادم مثلاً)
   * @param {Array} newPermissions - الصلاحيات الجديدة
   */
  const updatePermissions = (newPermissions) => {
    setPermissions(newPermissions);
    
    // تحديث بيانات المستخدم في التخزين
    try {
      const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        user.permissions = newPermissions;
        
        if (localStorage.getItem('user')) {
          localStorage.setItem('user', JSON.stringify(user));
        } else {
          sessionStorage.setItem('user', JSON.stringify(user));
        }
      }
    } catch (error) {
      logger.error('Error updating stored permissions:', error);
    }
  };

  const value = {
    permissions,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    updatePermissions,
    refreshPermissions: loadUserPermissions,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

/**
 * Hook للوصول إلى context الصلاحيات
 */
export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  
  return context;
};

export default PermissionsContext;
